import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions, selectors } from 'darwin_core';

import { useWebSocket } from '../transport/WebSocketProvider.jsx';
import { zoneControlStatus } from './zoneControlStatus.js';
import { sendableFromStatus } from './useControlsSendable.js';

// The shared control-state + dispatch layer every per-zone control is built on.
// It bundles three things so the controls stay thin and never re-derive them
// independently:
//
//   1. the optimistic lifecycle status of the zone's value (idle/pending/confirmed),
//   2. the gating signals (online, holiday-locked, socket sendable),
//   3. bound action dispatchers that thread the live `sendWsMessage` from the
//      transport plus the zone tuple into darwin_core's thunks.
//
// There is deliberately NO in-flight lock here. A command still on its way
// (darwin_core's isCommandInFlight, shown by the Zone header spinner) is a cue
// that tells the user when it is safe to leave — darwin_core takes a new command
// on top of one still out, so no control disables on it, exactly as in the app.
//
// darwin_core's control thunks take `sendWsMessage` as their first argument and
// are dispatched (redux-thunk); the saga is what actually forwards the built
// envelope to the socket. Reading `sendWsMessage` from the WebSocket context here
// keeps every control's call site identical to the mobile app's.
export function useZoneControls(homeId, zoneId) {
  const dispatch = useDispatch();
  const ws = useWebSocket();
  // The provider always supplies a guarded sendWsMessage; fall back to a no-op so
  // a control rendered without the transport (isolated tests) never throws.
  const sendWsMessage = ws?.sendWsMessage ?? noop;

  const status = useSelector((state) =>
    zoneControlStatus(state, homeId, zoneId),
  );
  const online = useSelector((state) =>
    selectors.isZoneOnline(state, homeId, zoneId),
  );
  const inHoliday = useSelector((state) =>
    // isInHolidayMode returns 3-state-ish values elsewhere, but for a real
    // heating zone it is a boolean; coerce so consumers get a clean flag.
    Boolean(selectors.isInHolidayMode(state, homeId, zoneId)),
  );

  // Whether our socket is Open, so a command would actually be sent rather than
  // silently dropped. Read from the same transport context sendWsMessage comes
  // from; every write control folds this into its disabled state.
  const connectionStatus = ws?.webSocketInfo?.webSocketStatus ?? null;
  const sendable = sendableFromStatus(connectionStatus);

  // Each control derives its own disabled state from these flags rather than a
  // single shared `locked`: the cancel-override exit, for instance, must stay
  // live in an override that locks the plain mode toggles.

  // Stable identity for the tuple so the bound callbacks are not rebuilt each
  // render (zoneId is a fresh array from the store on every dispatch).
  const prtId = zoneId?.[0];
  const endpointId = zoneId?.[1];

  const bound = useMemo(() => {
    const zone = [prtId, endpointId];
    return {
      setSetpoint: (setpoint) =>
        dispatch(actions.setSetpoint(sendWsMessage, homeId, zone, setpoint)),
      incrementSetpoint: () =>
        dispatch(actions.incrementSetpoint(sendWsMessage, homeId, zone)),
      decrementSetpoint: () =>
        dispatch(actions.decrementSetpoint(sendWsMessage, homeId, zone)),
      setMode: (mode) =>
        dispatch(actions.setMode(sendWsMessage, homeId, zone, mode)),
      setPlusHours: (holdDurationMinutes) =>
        dispatch(
          actions.setPlusHours(
            sendWsMessage,
            homeId,
            zone,
            holdDurationMinutes,
          ),
        ),
      cancelOverride: () =>
        dispatch(actions.cancelOverride(sendWsMessage, homeId, zone)),
    };
  }, [dispatch, sendWsMessage, homeId, prtId, endpointId]);

  return {
    status,
    online,
    inHoliday,
    sendable,
    connectionStatus,
    actions: bound,
  };
}

const noop = () => {};
